import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon-green.svg';
import { useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../utils/dashboardUtils";

interface DoubleMetricCardProps {
    metricOne: DisaggregatedMetric;
    metricTwo: DisaggregatedMetric;
    title: string;
    metadataId: number;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

// Add types to manage metadata state per metric
type MetricInfo = {
    show: boolean;
    metadata: MetricMetadata[] | null;
    distribution: RawIndicatorData[] | null;
    countyDistribution: DistributionData[] | null;
    loading: boolean;
    error: Error | null;
};

const DoubleMetricCard = ({ metricOne, metricTwo, title, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: DoubleMetricCardProps) => {
    const [metricOneInfo, setMetricOneInfo] = useState<MetricInfo>({
        show: false, metadata: null, distribution: null, countyDistribution: null, loading: false, error: null
    });

    const [metricTwoInfo, setMetricTwoInfo] = useState<MetricInfo>({
        show: false, metadata: null, distribution: null, countyDistribution: null, loading: false, error: null
    });

    const fetchAllMetadata = async (metricId: number, setInfo: React.Dispatch<React.SetStateAction<MetricInfo>>) => {
        setInfo(prev => ({ ...prev, show: true, loading: true }));

        try {
            const [metaRes, distRes, countyRes] = await Promise.all([
                fetchMetricMetadata(metricId),
                fetchIndicatorBinCount(metricId, geoDetails, geoTag),
                fetchDistributionData(metricId, [], null)
            ]);

            setInfo(prev => ({
                ...prev,
                metadata: metaRes?.data || null,
                distribution: distRes || null,
                countyDistribution: countyRes || null,
                loading: false,
                error: null
            }));
        } catch (err) {
            const errorMessage = (err as Error).message || 'Unexpected error';
            console.error('Error loading metric metadata:', errorMessage);
            setInfo(prev => ({ ...prev, loading: false, error: new Error(errorMessage) }));
        }
    };

    const handleClick = (metricId: number, info: MetricInfo, setInfo: React.Dispatch<React.SetStateAction<MetricInfo>>) => {
        if (info.show) {
            setInfo(prev => ({ ...prev, show: false }));
            return;
        }

        if (info.metadata && info.distribution && info.countyDistribution) {
            setInfo(prev => ({ ...prev, show: true }));
            return;
        }

        fetchAllMetadata(metricId, setInfo);
    };

    const renderMetric = (
        label: string,
        value: string | number,
        unit: string,
        metricId: number,
        info: MetricInfo,
        setInfo: React.Dispatch<React.SetStateAction<MetricInfo>>
    ) => (
        <div className="flex flex-col items-start ml-4">
            <div className="flex items-center">
                <p className="text-lacey-blue text-sm font-barlow-condensed font-bold">{label}</p>
                {showMetadataIcon && (
                    <button onClick={() => handleClick(metricId, info, setInfo)} className="ml-1">
                        <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                    </button>
                )}
            </div>
            <h2 className="text-lacey-blue text-3xl font-barlow-condensed font-bold -mt-1">
                {formatValue(value, unit, metricId)}
            </h2>
        </div>
    );

    const { versions: versions1 } = metricOne;
    const { value: value1, unit: unit1, percent_calc: percentCalc1, year: year1 } = versions1[0];
    const formattedValue1 = unit1 === "ratio" ? `${percentCalc1?.n}/${percentCalc1?.d}` : value1;

    const { versions: versions2 } = metricTwo;
    const { value: value2, unit: unit2, percent_calc: percentCalc2 } = versions2[0];
    const formattedValue2 = unit2 === "ratio" ? `${percentCalc2?.n}/${percentCalc2?.d}` : value2;

    return (
        <div className="mx-2 my-2">
            <div className="flex flex-col items-start w-full">
                <div className="flex items-center justify-start">
                    {svg && <img src={svg} alt={`${title} icon`} className="w-[50px] h-[50px]" />}
                    {renderMetric("Renters", formattedValue1, unit1, metricOne.id, metricOneInfo, setMetricOneInfo)}
                    {renderMetric("Owners", formattedValue2, unit2, metricTwo.id, metricTwoInfo, setMetricTwoInfo)}
                </div>
                <div className="flex mt-1 max-w-44">
                    <p className="text-lacey-text text-xs font-bold">{title}</p>
                </div>
                {/* {unitDescriptor1 && <p className="text-lacey-text text-xs -mt-1">{unitDescriptor1}</p>} */}
                {!showMetadataIcon && <p className="text-lacey-text text-xs w-full">in {year1}</p>}
            </div>

            {/* Modals */}
            <MetricMetadataModal
                isOpen={metricOneInfo.show}
                onClose={() => setMetricOneInfo(prev => ({ ...prev, show: false }))}
                id={metricOne.id}
                metadata={metricOneInfo.metadata}
                disaggData={disaggData}
                distributionData={metricOneInfo.distribution}
                countyDistributionData={metricOneInfo.countyDistribution}
                stateData={null}
                nationalData={null}
                selectionActive={selectionActive}
                svg={svg}
                loading={metricOneInfo.loading}
                error={metricOneInfo.error}
            />
            <MetricMetadataModal
                isOpen={metricTwoInfo.show}
                onClose={() => setMetricTwoInfo(prev => ({ ...prev, show: false }))}
                id={metricTwo.id}
                metadata={metricTwoInfo.metadata}
                disaggData={disaggData}
                distributionData={metricTwoInfo.distribution}
                countyDistributionData={metricTwoInfo.countyDistribution}
                stateData={null}
                nationalData={null}
                selectionActive={selectionActive}
                svg={svg}
                loading={metricTwoInfo.loading}
                error={metricTwoInfo.error}
            />
        </div>
    );
};



export default DoubleMetricCard;
